import { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText,
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Upload,
  Star,
  AlertTriangle,
  RefreshCw,
  Image,
  Download,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { PreviewPdfModal } from '../components/shared/PreviewPdfModal';
import type { PreviewPdfRequest, PreviewPdfSettings } from '../types/api';
import * as pdfjsLib from 'pdfjs-dist';
import { useCertificateDesigns, useGlobalFonts, useOrganisationList } from '../hooks/useApi';
import { Search } from 'lucide-react';
import { useEnv } from '../contexts/EnvContext';
import { api } from '../api/client';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import { StatusBadge } from '../components/shared/StatusBadge';
import { InputModal } from '../components/shared/InputModal';
import type {
  DiplomaDesignOverview,
  DiplomaDesignVariantInfo,
  GlobalFontInfo,
} from '../types/api';

import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;


const ALL_LANGUAGES = [
  'bg', 'cs', 'da', 'de', 'el', 'en', 'es', 'et', 'fi', 'fr',
  'ga', 'hr', 'hu', 'it', 'lt', 'lv', 'mt', 'nl', 'no', 'pl',
  'pt', 'ro', 'sk', 'sl', 'sr', 'sv', 'tr', 'uk',
];

// Sample data per language with locale-appropriate diacritics for PDF preview
const LOCALE_SAMPLE_DATA: Record<string, { ownerName: string; title: string; issuerName: string }> = {
  bg: { ownerName: 'Йордан Йорданов', title: 'Сертификат за уеб разработка', issuerName: 'Софийски Университет' },
  cs: { ownerName: 'Břetislav Čapek', title: 'Certifikát webového vývoje', issuerName: 'Česká Technická Univerzita' },
  da: { ownerName: 'Søren Kjærgaard', title: 'Certifikat i webudvikling', issuerName: 'Københavns Universitet' },
  de: { ownerName: 'Jürgen Müller', title: 'Zertifikat für Webentwicklung', issuerName: 'Technische Universität München' },
  el: { ownerName: 'Αλέξανδρος Παπαδόπουλος', title: 'Πιστοποιητικό Ανάπτυξης Ιστού', issuerName: 'Εθνικό Μετσόβιο Πολυτεχνείο' },
  en: { ownerName: 'James O\'Brien', title: 'Certificate in Web Development', issuerName: 'University of Oxford' },
  es: { ownerName: 'José María García', title: 'Certificado de Desarrollo Web', issuerName: 'Universidad Complutense de Madrid' },
  et: { ownerName: 'Tõnu Väärtnõu', title: 'Veebiarenduse tunnistus', issuerName: 'Tallinna Tehnikaülikool' },
  fi: { ownerName: 'Väinö Mäkelä', title: 'Web-kehityksen sertifikaatti', issuerName: 'Helsingin Yliopisto' },
  fr: { ownerName: 'François Lefèvre', title: 'Certificat de développement web', issuerName: 'Université Paris-Saclay' },
  ga: { ownerName: 'Séamus Ó Ceallaigh', title: 'Teastas i bhForbairt Gréasáin', issuerName: 'Ollscoil na hÉireann' },
  hr: { ownerName: 'Željko Čabrić', title: 'Certifikat za web razvoj', issuerName: 'Sveučilište u Zagrebu' },
  hu: { ownerName: 'Szőke Zsigmond', title: 'Webfejlesztési tanúsítvány', issuerName: 'Budapesti Műszaki Egyetem' },
  it: { ownerName: 'Giuseppe Caròla', title: 'Certificato di sviluppo web', issuerName: 'Politecnico di Milano' },
  lt: { ownerName: 'Žygimantas Čiurlionis', title: 'Žiniatinklio kūrimo pažymėjimas', issuerName: 'Vilniaus Universitetas' },
  lv: { ownerName: 'Jānis Bērziņš', title: 'Tīmekļa izstrādes sertifikāts', issuerName: 'Latvijas Universitāte' },
  mt: { ownerName: 'Ġużeppi Farruġia', title: 'Ċertifikat fl-Iżvilupp tal-Web', issuerName: 'Università ta\' Malta' },
  nl: { ownerName: 'François van der Wijk', title: 'Certificaat webontwikkeling', issuerName: 'Technische Universiteit Delft' },
  no: { ownerName: 'Bjørn Ødegård', title: 'Sertifikat i webutvikling', issuerName: 'Norges Teknisk-Naturvitenskapelige Universitet' },
  pl: { ownerName: 'Żelisław Żółć', title: 'Certyfikat tworzenia stron internetowych', issuerName: 'Politechnika Wrocławska' },
  pt: { ownerName: 'João Gonçalves', title: 'Certificado de desenvolvimento web', issuerName: 'Universidade de Coimbra' },
  ro: { ownerName: 'Ștefan Brâncoveanu', title: 'Certificat de dezvoltare web', issuerName: 'Universitatea din București' },
  sk: { ownerName: 'Ľubomír Kováč', title: 'Certifikát webového vývoja', issuerName: 'Slovenská Technická Univerzita' },
  sl: { ownerName: 'Boštjan Čopič', title: 'Certifikat za spletni razvoj', issuerName: 'Univerza v Ljubljani' },
  sr: { ownerName: 'Ђорђе Петровић', title: 'Сертификат за веб развој', issuerName: 'Универзитет у Београду' },
  sv: { ownerName: 'Björn Sjöström', title: 'Certifikat i webbutveckling', issuerName: 'Kungliga Tekniska Högskolan' },
  tr: { ownerName: 'Hüseyin Çağlar', title: 'Web Geliştirme Sertifikası', issuerName: 'İstanbul Teknik Üniversitesi' },
  uk: { ownerName: 'Олександр Грищенко', title: 'Сертифікат з веб-розробки', issuerName: 'Київський Національний Університет' },
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function uploadToSas(sasUrl: string, file: File, contentType: string) {
  const response = await fetch(sasUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': contentType,
    },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`);
  }
}

interface PdfAnnotation {
  subtype: string;
  fieldType?: string;
  fieldName?: string;
  rect: number[];
  viewportRect: number[]; // transformed to viewport space
  defaultAppearanceData?: {
    fontName?: string;
    fontSize?: number;
  };
}

interface PdfRenderResult {
  annotations: PdfAnnotation[];
  canvasWidth: number;
  canvasHeight: number;
}

async function renderPdf(
  url: string,
  canvas: HTMLCanvasElement
): Promise<PdfRenderResult> {
  const pdf = await pdfjsLib.getDocument({ url, isEvalSupported: false }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 1.5 });
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { annotations: [], canvasWidth: 0, canvasHeight: 0 };
  // @ts-expect-error pdfjs-dist v5 types require canvas but it works without
  await page.render({ canvasContext: ctx, viewport }).promise;

  const rawAnnotations = await page.getAnnotations();
  // Convert PDF-space rects to viewport-space using the viewport transform
  const annotations = rawAnnotations
    .filter((a: any) => a.subtype === 'Widget' && a.rect)
    .map((a: any) => {
      const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(a.rect);
      // convertToViewportRectangle may return flipped y coords
      return {
        ...a,
        viewportRect: [
          Math.min(x1, x2),
          Math.min(y1, y2),
          Math.max(x1, x2),
          Math.max(y1, y2),
        ],
      } as PdfAnnotation;
    });

  return { annotations, canvasWidth: viewport.width, canvasHeight: viewport.height };
}

// ---- Font Validation (using pdf-lib for AcroForm access) ----

import { PDFDocument, PDFName, PDFDict, PDFRef } from 'pdf-lib';

interface FontValidationResult {
  fieldName: string;
  resourceName: string | null;
  baseFont: string | null;
  family: string | null;
  fontSize: number | null;
  fontColor: string | null;
  status: 'EXACT_MATCH' | 'FAMILY_MATCH' | 'DESIGN_FALLBACK' | 'DEFAULT_FALLBACK' | 'NO_FONT_INFO';
  resolvedFont: string;
}

function parseDA(da: string): { resourceName: string | null; fontSize: number | null; color: string | null } {
  if (!da) return { resourceName: null, fontSize: null, color: null };
  const fontMatch = da.match(/\/([^\0\t\n\f\r ]+)[\0\t\n\f\r ]*(\d*\.?\d+)?[\0\t\n\f\r ]+Tf/);
  const resourceName = fontMatch ? fontMatch[1] : null;
  const fontSize = fontMatch && fontMatch[2] ? parseFloat(fontMatch[2]) : null;
  const rgbMatch = da.match(/([\d.]+)\s+([\d.]+)\s+([\d.]+)\s+rg/);
  const grayMatch = da.match(/([\d.]+)\s+g(?:\s|$)/);
  let color: string | null = null;
  if (rgbMatch) {
    const r = Math.round(parseFloat(rgbMatch[1]) * 255);
    const g = Math.round(parseFloat(rgbMatch[2]) * 255);
    const b = Math.round(parseFloat(rgbMatch[3]) * 255);
    color = `rgb(${r},${g},${b})`;
  } else if (grayMatch) {
    const v = Math.round(parseFloat(grayMatch[1]) * 255);
    color = `rgb(${v},${v},${v})`;
  }
  return { resourceName, fontSize, color };
}

function resolveBaseFont(resourceName: string, fontDict: PDFDict | null): string | null {
  if (!fontDict || !resourceName) return null;
  try {
    const fontRef = fontDict.get(PDFName.of(resourceName));
    if (!fontRef) return null;
    let fontObj: PDFDict | null = null;
    if (fontRef instanceof PDFDict) {
      fontObj = fontRef;
    } else if (fontRef instanceof PDFRef) {
      fontObj = fontDict.context.lookup(fontRef) as PDFDict;
    }
    if (!fontObj || !(fontObj instanceof PDFDict)) return null;
    const baseFontName = fontObj.get(PDFName.of('BaseFont'));
    if (!baseFontName) return null;
    let name = baseFontName.toString();
    // PDFName.toString() returns "/Name" — strip leading /
    if (name.startsWith('/')) name = name.substring(1);
    return name;
  } catch {
    return null;
  }
}

async function validatePdfFonts(
  pdfUrl: string,
  globalFontNames: string[],
  designFonts: { fontName: string; blobPath: string; isPrimary: boolean }[],
): Promise<FontValidationResult[]> {
  // Fetch PDF and load with pdf-lib
  const pdfBytes = await fetch(pdfUrl).then(r => r.arrayBuffer());
  const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });

  // Get AcroForm → DR → Font dictionary
  const catalog = pdfDoc.catalog;
  let fontDict: PDFDict | null = null;
  try {
    const acroFormRef = catalog.get(PDFName.of('AcroForm'));
    const acroForm = acroFormRef instanceof PDFRef
      ? catalog.context.lookup(acroFormRef) as PDFDict
      : acroFormRef as PDFDict;
    if (acroForm && acroForm instanceof PDFDict) {
      const drRef = acroForm.get(PDFName.of('DR'));
      const dr = drRef instanceof PDFRef
        ? catalog.context.lookup(drRef) as PDFDict
        : drRef as PDFDict;
      if (dr && dr instanceof PDFDict) {
        const fdRef = dr.get(PDFName.of('Font'));
        fontDict = fdRef instanceof PDFRef
          ? catalog.context.lookup(fdRef) as PDFDict
          : fdRef as PDFDict;
      }
    }
  } catch { /* no AcroForm DR */ }

  const form = pdfDoc.getForm();
  const fields = form.getFields();

  const primaryDesignFont = designFonts.find(f => f.isPrimary);
  const designFallbackName = primaryDesignFont
    ? primaryDesignFont.blobPath.split('/').pop()?.replace(/\.ttf$/i, '') || primaryDesignFont.fontName
    : null;

  const registryNames = globalFontNames.map(n => n.replace(/\.(ttf|otf)$/i, ''));
  const results: FontValidationResult[] = [];

  for (const field of fields) {
    const fieldName = field.getName();
    // Skip image fields — they don't use fonts
    if (/image/i.test(fieldName)) continue;
    // Get DA from the field's acroField
    const acroField = (field as any).acroField;
    let daString = '';
    try {
      daString = acroField?.getDefaultAppearance?.() || '';
    } catch { /* no DA */ }

    const { resourceName, fontSize, color } = parseDA(daString);

    if (!resourceName) {
      results.push({
        fieldName,
        resourceName: null,
        baseFont: null,
        family: null,
        fontSize,
        fontColor: color,
        status: 'NO_FONT_INFO',
        resolvedFont: designFallbackName ? `${designFallbackName}.ttf` : 'Poppins-Bold.ttf',
      });
      continue;
    }

    // Resolve resource name → BaseFont via DR font dictionary
    let baseFont = resolveBaseFont(resourceName, fontDict) || resourceName;

    // Strip subset prefix: ABCDEF+FontName → FontName
    if (/^[A-Z]{6}\+/.test(baseFont)) {
      baseFont = baseFont.substring(7);
    }
    baseFont = baseFont.replace(/,/g, '-');

    const family = baseFont.split('-')[0];

    // Check global registry
    const exactMatch = registryNames.some(n => n === baseFont);
    const familyMatch = !exactMatch && registryNames.some(n => n === family);

    // Check design fonts (by fontName or blob filename)
    const designFontNames = designFonts.map(f => {
      const blobName = f.blobPath.split('/').pop()?.replace(/\.ttf$/i, '') || '';
      return { fontName: f.fontName, blobName, isPrimary: f.isPrimary };
    });
    const designExactMatch = designFontNames.find(
      df => df.blobName === baseFont || df.fontName === baseFont ||
            df.blobName === family || df.fontName === family
    );

    let status: FontValidationResult['status'];
    let resolvedFont: string;

    if (exactMatch) {
      status = 'EXACT_MATCH';
      resolvedFont = `${baseFont}.ttf`;
    } else if (familyMatch) {
      status = 'FAMILY_MATCH';
      resolvedFont = `${family}.ttf`;
    } else if (designExactMatch) {
      status = 'EXACT_MATCH'; // design font matches — this is correct
      resolvedFont = `${designExactMatch.blobName}.ttf`;
    } else if (designFallbackName) {
      status = 'DESIGN_FALLBACK';
      resolvedFont = `${designFallbackName}.ttf`;
    } else {
      status = 'DEFAULT_FALLBACK';
      resolvedFont = 'Poppins-Bold.ttf';
    }

    results.push({
      fieldName,
      resourceName,
      baseFont,
      family,
      fontSize,
      fontColor: color,
      status,
      resolvedFont,
    });
  }

  return results;
}

const STATUS_CONFIG = {
  EXACT_MATCH: { icon: '✅', label: 'Exact match', color: 'text-emerald-600 dark:text-emerald-400' },
  FAMILY_MATCH: { icon: '✅', label: 'Family match', color: 'text-emerald-600 dark:text-emerald-400' },
  DESIGN_FALLBACK: { icon: '⚠️', label: 'Design fallback', color: 'text-amber-600 dark:text-amber-400' },
  DEFAULT_FALLBACK: { icon: '⚠️', label: 'Default fallback', color: 'text-amber-600 dark:text-amber-400' },
  NO_FONT_INFO: { icon: '❓', label: 'No font info', color: 'text-gray-500 dark:text-gray-400' },
};

// ---- Design Card ----

function DesignCard({
  design,
  onClick,
}: {
  design: DiplomaDesignOverview;
  onClick: () => void;
}) {
  const existingLangs = new Set(design.variants.map((v) => v.language));

  return (
    <button
      type="button"
      onClick={onClick}
      className="card w-full text-left hover:ring-2 hover:ring-brand-300 dark:hover:ring-brand-600 transition-all cursor-pointer"
    >
      {/* Thumbnail */}
      <div className="w-full h-32 rounded-lg mb-3 overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
        {design.thumbnailUrl ? (
          <img
            src={design.thumbnailUrl}
            alt={design.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <Image className="h-10 w-10 text-gray-300 dark:text-gray-600" />
        )}
      </div>

      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
          {design.name}
        </h3>
        <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
          #{design.sortOrder}
        </span>
      </div>

      {design.type === 'organisation' && design.organisationName && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
          {design.organisationName}
        </p>
      )}

      <div className="flex items-center gap-2 mt-1.5">
        <StatusBadge
          label={design.type === 'system' ? 'System' : 'Org'}
          variant={design.type === 'system' ? 'info' : 'neutral'}
        />
        <StatusBadge
          label={design.isActive ? 'Aktywny' : 'Nieaktywny'}
          variant={design.isActive ? 'success' : 'neutral'}
        />
      </div>

      {/* Language dots */}
      <div className="flex flex-wrap gap-1 mt-3">
        {ALL_LANGUAGES.map((lang) => (
          <span
            key={lang}
            className={`inline-block w-2 h-2 rounded-full ${
              existingLangs.has(lang)
                ? 'bg-emerald-500'
                : 'bg-gray-300 dark:bg-gray-600'
            }`}
            title={lang}
          />
        ))}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
        Uzywany w {design.usageCount} szablonach
      </p>
    </button>
  );
}

// ---- Gallery View ----

function GalleryView({
  onSelect,
}: {
  onSelect: (design: DiplomaDesignOverview) => void;
}) {
  const { env } = useEnv();
  const queryClient = useQueryClient();
  const { data: designs, isLoading, error, refetch } = useCertificateDesigns();
  const {
    data: globalFonts,
    isLoading: fontsLoading,
    error: fontsError,
    refetch: refetchFonts,
  } = useGlobalFonts();

  const { data: organisations } = useOrganisationList();

  const [showArchived, setShowArchived] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createSlug, setCreateSlug] = useState('');
  const [createType, setCreateType] = useState('system');
  const [createOrgId, setCreateOrgId] = useState<string | null>(null);
  const [createOrgName, setCreateOrgName] = useState('');
  const [orgSearch, setOrgSearch] = useState('');
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const orgDropdownRef = useRef<HTMLDivElement>(null);

  const [isUploadingFont, setIsUploadingFont] = useState(false);
  const [isDeletingFont, setIsDeletingFont] = useState<string | null>(null);
  const [isInvalidatingCache, setIsInvalidatingCache] = useState(false);
  const [fontError, setFontError] = useState<string | null>(null);
  const [cacheMessage, setCacheMessage] = useState<string | null>(null);
  const fontInputRef = useRef<HTMLInputElement>(null);

  const filteredOrgs = (organisations ?? []).filter((org) =>
    org.name.toLowerCase().includes(orgSearch.toLowerCase())
  );

  // Close org dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target as Node)) {
        setShowOrgDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreate = async () => {
    if (!createName || !createSlug) return;
    if (createType === 'organisation' && !createOrgId) return;
    setIsCreating(true);
    setCreateError(null);
    try {
      const newDesign = await api.createCertificateDesign(
        {
          name: createName,
          slug: createSlug,
          type: createType,
          ...(createType === 'organisation' && createOrgId ? { organisationId: createOrgId } : {}),
        },
        env
      );
      await queryClient.invalidateQueries({ queryKey: ['certificate-designs'] });
      setShowCreateForm(false);
      setCreateName('');
      setCreateSlug('');
      setCreateType('system');
      setCreateOrgId(null);
      setCreateOrgName('');
      setOrgSearch('');
      onSelect(newDesign);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Blad tworzenia');
    } finally {
      setIsCreating(false);
    }
  };

  const handleFontUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setIsUploadingFont(true);
    setFontError(null);
    try {
      const fontName = file.name.replace(/\.ttf$/i, '');
      const sasResponse = await api.createGlobalFontUpload(fontName, env);
      await uploadToSas(sasResponse.sasUrl, file, 'font/ttf');
      await queryClient.invalidateQueries({ queryKey: ['global-fonts'] });
    } catch (err) {
      setFontError(err instanceof Error ? err.message : 'Blad uploadu czcionki');
    } finally {
      setIsUploadingFont(false);
    }
  };

  const handleDeleteFont = async (fontName: string) => {
    if (!confirm(`Usunac czcionke "${fontName}"?`)) return;
    setIsDeletingFont(fontName);
    setFontError(null);
    try {
      await api.deleteGlobalFont(fontName, env);
      await queryClient.invalidateQueries({ queryKey: ['global-fonts'] });
    } catch (err) {
      setFontError(err instanceof Error ? err.message : 'Blad usuwania czcionki');
    } finally {
      setIsDeletingFont(null);
    }
  };

  const handleInvalidateCache = async () => {
    setIsInvalidatingCache(true);
    setCacheMessage(null);
    try {
      const result = await api.invalidateFontCache(env);
      setCacheMessage(
        result.cleared
          ? `Cache wyczyszczony (${result.entriesRemoved ?? 0} wpisow)`
          : `Blad: ${result.error || 'nieznany'}`
      );
      setTimeout(() => setCacheMessage(null), 5000);
    } catch (err) {
      setCacheMessage(err instanceof Error ? err.message : 'Blad invalidacji');
    } finally {
      setIsInvalidatingCache(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Designs Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Designs
            </h2>
            <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
              <button
                type="button"
                onClick={() => setShowArchived(false)}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  !showArchived
                    ? 'bg-brand-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Aktywne
              </button>
              <button
                type="button"
                onClick={() => setShowArchived(true)}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  showArchived
                    ? 'bg-brand-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Zarchiwizowane
              </button>
            </div>
          </div>
          {!showArchived && (
            <button
              type="button"
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="btn-primary text-sm flex items-center gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Nowy design
            </button>
          )}
        </div>

        {showCreateForm && (
          <div className="card mb-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Nowy design
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nazwa
                </label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  className="input-field w-full"
                  placeholder="np. Certyfikat podstawowy"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Slug
                </label>
                <input
                  type="text"
                  value={createSlug}
                  onChange={(e) => setCreateSlug(e.target.value)}
                  className="input-field w-full"
                  placeholder="np. certyfikat-podstawowy"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Typ
                </label>
                <select
                  value={createType}
                  onChange={(e) => {
                    setCreateType(e.target.value);
                    if (e.target.value === 'system') {
                      setCreateOrgId(null);
                      setCreateOrgName('');
                      setOrgSearch('');
                    }
                  }}
                  className="input-field w-full"
                >
                  <option value="system">System</option>
                  <option value="organisation">Organizacja</option>
                </select>
              </div>
            </div>

            {createType === 'organisation' && (
              <div ref={orgDropdownRef} className="relative">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Organizacja
                </label>
                {createOrgId ? (
                  <div className="flex items-center gap-2">
                    <span className="input-field w-full flex items-center text-sm">
                      {createOrgName}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setCreateOrgId(null);
                        setCreateOrgName('');
                        setOrgSearch('');
                      }}
                      className="text-xs text-red-500 hover:text-red-700 whitespace-nowrap"
                    >
                      Zmien
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={orgSearch}
                        onChange={(e) => {
                          setOrgSearch(e.target.value);
                          setShowOrgDropdown(true);
                        }}
                        onFocus={() => setShowOrgDropdown(true)}
                        className="input-field w-full pl-8"
                        placeholder="Szukaj organizacji..."
                      />
                    </div>
                    {showOrgDropdown && (
                      <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                        {filteredOrgs.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            {organisations ? 'Brak wynikow' : 'Ladowanie...'}
                          </div>
                        ) : (
                          filteredOrgs.map((org) => (
                            <button
                              key={org.id}
                              type="button"
                              onClick={() => {
                                setCreateOrgId(org.id);
                                setCreateOrgName(org.name);
                                setOrgSearch('');
                                setShowOrgDropdown(false);
                              }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                              {org.name}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCreate}
                disabled={isCreating || !createName || !createSlug || (createType === 'organisation' && !createOrgId)}
                className="btn-primary text-sm disabled:opacity-50"
              >
                {isCreating ? 'Tworzenie...' : 'Utworz'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="btn-secondary text-sm"
              >
                Anuluj
              </button>
            </div>
            {createError && <ErrorAlert message={createError} />}
          </div>
        )}

        {isLoading && <LoadingSpinner label="Ladowanie designow..." />}
        {error && <ErrorAlert message={error.message} onRetry={() => refetch()} />}
        {designs && (() => {
          const filtered = designs.filter((d) => showArchived ? !d.isActive : d.isActive);
          if (filtered.length === 0) return (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              {showArchived ? 'Brak zarchiwizowanych designow' : 'Brak aktywnych designow'}
            </div>
          );
          return (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((design) => (
                <DesignCard
                  key={design.id}
                  design={design}
                  onClick={() => onSelect(design)}
                />
              ))}
          </div>
          );
        })()}
      </section>

      {/* Global Fonts Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Czcionki globalne
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleInvalidateCache}
              disabled={isInvalidatingCache}
              className="btn-secondary text-sm flex items-center gap-1.5"
            >
              <RefreshCw className={`h-4 w-4 ${isInvalidatingCache ? 'animate-spin' : ''}`} />
              {isInvalidatingCache ? 'Czyszczenie...' : 'Invaliduj cache'}
            </button>
            <input
              ref={fontInputRef}
              type="file"
              accept=".ttf"
              onChange={handleFontUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fontInputRef.current?.click()}
              disabled={isUploadingFont}
              className="btn-primary text-sm flex items-center gap-1.5"
            >
              <Upload className="h-4 w-4" />
              {isUploadingFont ? 'Uploading...' : 'Dodaj czcionke'}
            </button>
          </div>
        </div>

        {cacheMessage && (
          <div className="mb-3 text-sm text-emerald-600 dark:text-emerald-400">
            {cacheMessage}
          </div>
        )}
        {fontError && <ErrorAlert message={fontError} />}

        {fontsLoading && <LoadingSpinner size="sm" />}
        {fontsError && (
          <ErrorAlert
            message={fontsError.message}
            onRetry={() => refetchFonts()}
          />
        )}
        {globalFonts && (
          <div className="card overflow-hidden p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-3">
                    Nazwa czcionki
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-3">
                    Rozmiar
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-3">
                    Ostatnia modyfikacja
                  </th>
                  <th className="text-right text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-3">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody>
                {globalFonts.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      Brak czcionek globalnych
                    </td>
                  </tr>
                )}
                {globalFonts.map((font: GlobalFontInfo) => (
                  <tr
                    key={font.name}
                    className="border-b border-gray-100 dark:border-gray-800 last:border-0"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      {font.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {formatFileSize(font.size)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {new Date(font.lastModified).toLocaleString('pl-PL')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleDeleteFont(font.name)}
                        disabled={isDeletingFont === font.name}
                        className="btn-danger text-xs"
                      >
                        {isDeletingFont === font.name ? '...' : 'Usun'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ---- Detail View ----

function DesignDetailView({
  initialDesign,
  onBack,
}: {
  initialDesign: DiplomaDesignOverview;
  onBack: () => void;
}) {
  const { env } = useEnv();
  const queryClient = useQueryClient();
  const { data: globalFontsData } = useGlobalFonts();

  const { data: organisations } = useOrganisationList();

  const [design, setDesign] = useState(initialDesign);
  const [editName, setEditName] = useState(design.name);
  const [editSortOrder, setEditSortOrder] = useState(design.sortOrder);
  const [editType, setEditType] = useState(design.type);
  const [editOrgId, setEditOrgId] = useState<string | null>(design.organisationId);
  const [editOrgName, setEditOrgName] = useState(design.organisationName ?? '');
  const [editOrgSearch, setEditOrgSearch] = useState('');
  const [showEditOrgDropdown, setShowEditOrgDropdown] = useState(false);
  const editOrgDropdownRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(design.isActive);

  // PDF Preview state
  const [previewModal, setPreviewModal] = useState<{ variantId: string; language: string } | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewSettings, setPreviewSettings] = useState<PreviewPdfSettings | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const filteredEditOrgs = (organisations ?? []).filter((org) =>
    org.name.toLowerCase().includes(editOrgSearch.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (editOrgDropdownRef.current && !editOrgDropdownRef.current.contains(e.target as Node)) {
        setShowEditOrgDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Thumbnail
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
  const [isDraggingThumbnail, setIsDraggingThumbnail] = useState(false);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  // Variants
  const [isUploadingVariant, setIsUploadingVariant] = useState<string | null>(null);
  const [isDeletingVariant, setIsDeletingVariant] = useState<string | null>(null);
  const [showAddVariant, setShowAddVariant] = useState(false);

  // PDF Preview
  const [previewVariant, setPreviewVariant] = useState<string>(
    design.variants.length > 0 ? design.variants[0].language : ''
  );
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [pdfAnnotations, setPdfAnnotations] = useState<PdfAnnotation[]>([]);
  const [pdfViewport, setPdfViewport] = useState<{ width: number; height: number } | null>(null);
  const [isRenderingPdf, setIsRenderingPdf] = useState(false);

  // Fonts
  const [isUploadingFont, setIsUploadingFont] = useState(false);
  const [isDeletingDesignFont, setIsDeletingDesignFont] = useState<string | null>(null);
  const [fontValidation, setFontValidation] = useState<FontValidationResult[] | null>(null);
  const [isValidatingFonts, setIsValidatingFonts] = useState(false);
  const fontInputRef = useRef<HTMLInputElement>(null);
  const variantInputRef = useRef<HTMLInputElement>(null);

  // Font name modals
  const [fontNameModal, setFontNameModal] = useState<{
    mode: 'upload' | 'rename';
    defaultValue: string;
    fontId?: string;
    file?: File;
  } | null>(null);

  // Delete
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [actionError, setActionError] = useState<string | null>(null);

  // Bulk upload
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState('');
  const [bulkUploadHasErrors, setBulkUploadHasErrors] = useState(false);

  // Download all PDFs
  const [isDownloadingAllPdfs, setIsDownloadingAllPdfs] = useState(false);
  const [downloadAllProgress, setDownloadAllProgress] = useState('');

  // Refresh design data
  const refreshDesign = useCallback(async () => {
    try {
      const updated = await api.getCertificateDesign(design.slug, env);
      setDesign(updated);
      setEditName(updated.name);
      setEditSortOrder(updated.sortOrder);
      setIsActive(updated.isActive);
      setEditType(updated.type);
      setEditOrgId(updated.organisationId);
      setEditOrgName(updated.organisationName ?? '');
    } catch {
      // ignore
    }
    await queryClient.invalidateQueries({ queryKey: ['certificate-designs'] });
  }, [design.slug, env, queryClient]);

  // Save metadata
  const handleSaveMetadata = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await api.updateCertificateDesign(
        design.slug,
        {
          name: editName,
          sortOrder: editSortOrder,
          isActive,
          type: editType,
          ...(editType === 'organisation' && editOrgId ? { organisationId: editOrgId } : { organisationId: null }),
        },
        env
      );
      await refreshDesign();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Blad zapisu');
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle active
  const handleToggleActive = async () => {
    const newActive = !isActive;
    setIsActive(newActive);
    try {
      await api.updateCertificateDesign(design.slug, { isActive: newActive }, env);
      await refreshDesign();
    } catch (err) {
      setIsActive(!newActive);
      setActionError(err instanceof Error ? err.message : 'Blad zmiany statusu');
    }
  };

  // Thumbnail upload
  const handleThumbnailUpload = async (file: File) => {
    setIsUploadingThumbnail(true);
    setActionError(null);
    try {
      const sasResponse = await api.createThumbnailUpload(design.slug, env);
      await uploadToSas(sasResponse.sasUrl, file, 'image/png');
      await refreshDesign();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Blad uploadu miniaturki');
    } finally {
      setIsUploadingThumbnail(false);
    }
  };

  const handleThumbnailDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDraggingThumbnail(false);
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        handleThumbnailUpload(file);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [design.slug, env]
  );

  // Variant upload
  const handleVariantUpload = async (lang: string, file: File) => {
    setIsUploadingVariant(lang);
    setActionError(null);
    try {
      const sasResponse = await api.createVariantUpload(design.slug, lang, env);
      await uploadToSas(sasResponse.sasUrl, file, 'application/pdf');
      await refreshDesign();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Blad uploadu wariantu');
    } finally {
      setIsUploadingVariant(null);
    }
  };

  const handleBulkUpload = async (file: File) => {
    // Frontend validation
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setActionError('Plik musi byc archiwum ZIP');
      return;
    }
    const MAX_ZIP_SIZE = 50 * 1024 * 1024; // 50 MB
    if (file.size > MAX_ZIP_SIZE) {
      setActionError(`Plik ZIP jest za duzy (max ${MAX_ZIP_SIZE / 1024 / 1024} MB)`);
      return;
    }

    setIsBulkUploading(true);
    setBulkUploadProgress('Uploading ZIP...');
    setBulkUploadHasErrors(false);
    setActionError(null);

    try {
      const result = await api.bulkUploadVariants(design.slug, file, env);

      // Build summary message
      const parts: string[] = [];
      if (result.uploaded.length > 0) {
        parts.push(`Wgrano ${result.uploaded.length} wariantow (${result.uploaded.join(', ')})`);
      }
      if (result.skipped.length > 0) {
        parts.push(`Pominieto ${result.skipped.length} plikow`);
      }
      if (result.errors.length > 0) {
        parts.push(`Bledy: ${result.errors.map(e => `${e.file}: ${e.reason}`).join('; ')}`);
      }
      if (result.uploaded.length === 0 && result.errors.length === 0) {
        parts.push('Nie znaleziono pasujacych plikow PDF w archiwum ZIP');
      }

      setBulkUploadProgress(parts.join('. '));
      setBulkUploadHasErrors(result.errors.length > 0);
      await refreshDesign();

      // Auto-clear success message after 8 seconds
      setTimeout(() => setBulkUploadProgress(''), 8000);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Blad bulk uploadu');
      setBulkUploadProgress('');
    } finally {
      setIsBulkUploading(false);
    }
  };

  const handleDownloadAllPdfs = async () => {
    if (design.variants.length === 0) {
      setActionError('Brak wariantow jezykowych do wygenerowania');
      return;
    }

    setIsDownloadingAllPdfs(true);
    setDownloadAllProgress(`Generowanie PDF 0/${design.variants.length}...`);
    setActionError(null);

    try {
      const pdfBuffers: { lang: string; data: ArrayBuffer }[] = [];
      const errors: string[] = [];

      // Generate PDFs sequentially to avoid overwhelming the API
      for (let i = 0; i < design.variants.length; i++) {
        const variant = design.variants[i];
        const lang = variant.language;
        const sample = LOCALE_SAMPLE_DATA[lang] || LOCALE_SAMPLE_DATA['en'];
        setDownloadAllProgress(`Generowanie PDF ${i + 1}/${design.variants.length} (${lang.toUpperCase()})...`);

        try {
          const result = await api.previewPdf(design.slug, variant.id, {
            ownerName: sample.ownerName,
            title: sample.title,
            issuerName: sample.issuerName,
            language: lang,
          }, env);

          // Fetch the generated PDF as ArrayBuffer
          const pdfResponse = await fetch(result.previewUrl);
          if (!pdfResponse.ok) throw new Error(`HTTP ${pdfResponse.status}`);
          const data = await pdfResponse.arrayBuffer();
          pdfBuffers.push({ lang, data });
        } catch (err) {
          errors.push(`${lang}: ${err instanceof Error ? err.message : 'nieznany blad'}`);
        }
      }

      if (pdfBuffers.length === 0) {
        setActionError(`Nie udalo sie wygenerowac zadnego PDF. Bledy: ${errors.join('; ')}`);
        return;
      }

      // Merge all PDFs into one using pdf-lib
      setDownloadAllProgress('Laczenie PDFow...');
      const mergedPdf = await PDFDocument.create();

      for (const { data } of pdfBuffers) {
        try {
          const sourcePdf = await PDFDocument.load(data);
          const pages = await mergedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
          for (const page of pages) {
            mergedPdf.addPage(page);
          }
        } catch {
          // Skip corrupted PDFs silently - already counted in errors
        }
      }

      const mergedBytes = await mergedPdf.save();
      const blob = new Blob([mergedBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = `${design.slug}-all-languages-preview.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const summary = [`Pobrano ${pdfBuffers.length}/${design.variants.length} PDF`];
      if (errors.length > 0) {
        summary.push(`Bledy (${errors.length}): ${errors.join('; ')}`);
      }
      setDownloadAllProgress(summary.join('. '));
      setTimeout(() => setDownloadAllProgress(''), 8000);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Blad generowania PDFow');
    } finally {
      setIsDownloadingAllPdfs(false);
    }
  };

  const handleDeleteVariant = async (lang: string) => {
    if (!confirm(`Usunac wariant "${lang}"?`)) return;
    setIsDeletingVariant(lang);
    setActionError(null);
    try {
      await api.deleteVariant(design.slug, lang, env);
      await refreshDesign();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Blad usuwania wariantu');
    } finally {
      setIsDeletingVariant(null);
    }
  };

  const handlePreviewPdf = async (body: PreviewPdfRequest) => {
    if (!previewModal) return;
    setIsGeneratingPreview(true);
    setPreviewError(null);
    try {
      const result = await api.previewPdf(design.slug, previewModal.variantId, body, env);
      setPreviewUrl(result.previewUrl);
      setPreviewSettings(result.settings);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Blad generowania PDF');
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const openPreviewModal = async (variantId: string, language: string) => {
    setPreviewModal({ variantId, language });
    setPreviewUrl(null);
    setPreviewSettings(null);
    setPreviewError(null);
    // Fetch saved settings immediately so the form is pre-filled
    try {
      const result = await api.previewPdf(design.slug, variantId, {}, env);
      setPreviewUrl(result.previewUrl);
      setPreviewSettings(result.settings);
    } catch {
      // Silent — user can still fill in manually and click generate
    }
  };

  const handleUploadPreviewImage = async (imageType: 'issuer' | 'badge', file: File): Promise<string> => {
    const sasResponse = await api.createPreviewImageUpload(imageType, env);
    await uploadToSas(sasResponse.sasUrl, file, file.type || 'image/png');
    return sasResponse.blobPath;
  };

  const handleAddVariant = (lang: string) => {
    setShowAddVariant(false);
    setPreviewVariant(lang);
    // Trigger file input for this language
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) handleVariantUpload(lang, file);
    };
    input.click();
  };

  // PDF rendering
  useEffect(() => {
    if (!previewVariant || !canvasRef.current) return;
    const variant = design.variants.find((v) => v.language === previewVariant);
    if (!variant?.pdfUrl) {
      setPdfAnnotations([]);
      setPdfViewport(null);
      return;
    }

    let cancelled = false;
    setIsRenderingPdf(true);

    (async () => {
      try {
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        const result = await renderPdf(variant.pdfUrl!, canvas);
        if (!cancelled) {
          setPdfAnnotations(result.annotations);
          setPdfViewport({ width: result.canvasWidth, height: result.canvasHeight });
        }
      } catch {
        // PDF render error - silently ignore
      } finally {
        if (!cancelled) setIsRenderingPdf(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [previewVariant, design.variants]);

  // Design font upload — step 1: show modal for name
  const handleDesignFontUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const defaultName = file.name.replace(/\.ttf$/i, '');
    setFontNameModal({ mode: 'upload', defaultValue: defaultName, file });
  };

  // Design font upload — step 2: after modal confirm
  const handleFontUploadConfirm = async (fontName: string) => {
    const file = fontNameModal?.file;
    setFontNameModal(null);
    if (!file) return;
    setIsUploadingFont(true);
    setActionError(null);
    try {
      const sasResponse = await api.createDesignFontUpload(design.slug, fontName.trim(), env);
      await uploadToSas(sasResponse.sasUrl, file, 'font/ttf');
      await refreshDesign();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Blad uploadu czcionki');
    } finally {
      setIsUploadingFont(false);
    }
  };

  // Rename — step 1: show modal
  const handleRenameFont = (fontId: string, currentName: string) => {
    setFontNameModal({ mode: 'rename', defaultValue: currentName, fontId });
  };

  // Rename — step 2: after modal confirm
  const handleRenameConfirm = async (newName: string) => {
    const fontId = fontNameModal?.fontId;
    setFontNameModal(null);
    if (!fontId) return;
    setActionError(null);
    try {
      await api.renameDesignFont(design.slug, fontId, newName, env);
      await refreshDesign();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Blad zmiany nazwy');
    }
  };

  const handleValidateFonts = async () => {
    if (!previewVariant) return;
    const variant = design.variants.find(v => v.language === previewVariant);
    if (!variant?.pdfUrl) return;

    setIsValidatingFonts(true);
    setFontValidation(null);
    try {
      const globalNames = (globalFontsData || []).map(f => f.name);
      const designFontList = design.fonts.map(f => ({
        fontName: f.fontName,
        blobPath: f.blobPath,
        isPrimary: f.isPrimary,
      }));
      const results = await validatePdfFonts(variant.pdfUrl, globalNames, designFontList);
      setFontValidation(results);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Blad walidacji czcionek');
    } finally {
      setIsValidatingFonts(false);
    }
  };

  const handleDeleteDesignFont = async (fontId: string) => {
    if (!confirm('Usunac czcionke?')) return;
    setIsDeletingDesignFont(fontId);
    setActionError(null);
    try {
      await api.deleteDesignFont(design.slug, fontId, env);
      await refreshDesign();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Blad usuwania czcionki');
    } finally {
      setIsDeletingDesignFont(null);
    }
  };

  // Delete design
  const handleDeleteDesign = async () => {
    setIsDeleting(true);
    setActionError(null);
    try {
      await api.deleteCertificateDesign(design.slug, env);
      await queryClient.invalidateQueries({ queryKey: ['certificate-designs'] });
      onBack();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Blad usuwania designu');
    } finally {
      setIsDeleting(false);
    }
  };

  const existingLangs = design.variants.map((v) => v.language);
  const availableLangs = ALL_LANGUAGES.filter((l) => !existingLangs.includes(l));

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="btn-secondary text-sm flex items-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Powrot
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              {design.name}
            </h2>
            {design.type === 'organisation' && design.organisationName && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {design.organisationName}
              </p>
            )}
          </div>
          <StatusBadge
            label={design.type === 'system' ? 'System' : 'Org'}
            variant={design.type === 'system' ? 'info' : 'neutral'}
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {isActive ? 'Aktywny' : 'Nieaktywny'}
          </span>
          <button
            type="button"
            onClick={handleToggleActive}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              isActive ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isActive ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </label>
      </div>

      {actionError && <ErrorAlert message={actionError} />}

      {/* Metadata */}
      <div className="card space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Metadane
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nazwa
            </label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Slug
            </label>
            <code className="block input-field w-full bg-gray-50 dark:bg-gray-800 cursor-not-allowed text-gray-500">
              {design.slug}
            </code>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Sort Order
            </label>
            <input
              type="number"
              value={editSortOrder}
              onChange={(e) => setEditSortOrder(Number(e.target.value))}
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Typ
            </label>
            <select
              value={editType}
              onChange={(e) => {
                setEditType(e.target.value);
                if (e.target.value === 'system') {
                  setEditOrgId(null);
                  setEditOrgName('');
                  setEditOrgSearch('');
                }
              }}
              className="input-field w-full"
            >
              <option value="system">System</option>
              <option value="organisation">Organizacja</option>
            </select>
          </div>
          {editType === 'organisation' && (
            <div ref={editOrgDropdownRef} className="relative">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Organizacja
              </label>
              {editOrgId ? (
                <div className="flex items-center gap-2">
                  <span className="input-field w-full flex items-center text-sm">
                    {editOrgName}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setEditOrgId(null);
                      setEditOrgName('');
                      setEditOrgSearch('');
                    }}
                    className="text-xs text-red-500 hover:text-red-700 whitespace-nowrap"
                  >
                    Zmien
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={editOrgSearch}
                      onChange={(e) => {
                        setEditOrgSearch(e.target.value);
                        setShowEditOrgDropdown(true);
                      }}
                      onFocus={() => setShowEditOrgDropdown(true)}
                      className="input-field w-full pl-8"
                      placeholder="Szukaj organizacji..."
                    />
                  </div>
                  {showEditOrgDropdown && (
                    <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                      {filteredEditOrgs.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          {organisations ? 'Brak wynikow' : 'Ladowanie...'}
                        </div>
                      ) : (
                        filteredEditOrgs.map((org) => (
                          <button
                            key={org.id}
                            type="button"
                            onClick={() => {
                              setEditOrgId(org.id);
                              setEditOrgName(org.name);
                              setEditOrgSearch('');
                              setShowEditOrgDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                          >
                            {org.name}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSaveMetadata}
            disabled={isSaving}
            className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {isSaving ? 'Zapisywanie...' : 'Zapisz'}
          </button>
          {saveSuccess && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400">
              Zapisano
            </span>
          )}
          {saveError && (
            <span className="text-xs text-red-600 dark:text-red-400">
              {saveError}
            </span>
          )}
        </div>
      </div>

      {/* Thumbnail */}
      <div className="card space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Miniaturka
        </h3>
        <div className="flex items-start gap-4">
          <div className="w-32 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
            {design.thumbnailUrl ? (
              <img
                src={design.thumbnailUrl}
                alt="Thumbnail"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500">Brak</span>
            )}
          </div>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDraggingThumbnail(true);
            }}
            onDragLeave={() => setIsDraggingThumbnail(false)}
            onDrop={handleThumbnailDrop}
            onClick={() => thumbnailInputRef.current?.click()}
            className={`flex-1 border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
              isDraggingThumbnail
                ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
          >
            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleThumbnailUpload(file);
                e.target.value = '';
              }}
            />
            <Upload className={`h-6 w-6 mx-auto mb-1 ${isDraggingThumbnail ? 'text-brand-500' : 'text-gray-400'}`} />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isUploadingThumbnail
                ? 'Uploading...'
                : 'Przeciagnij plik PNG lub kliknij aby wybrac'}
            </p>
          </div>
        </div>
      </div>

      {/* Language Variants */}
      <div className="card space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Warianty jezykowe
          </h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownloadAllPdfs}
              disabled={isDownloadingAllPdfs || design.variants.length === 0}
              className="btn-secondary text-sm flex items-center gap-1"
              title="Generuje PDF preview dla kazdego jezyka z lokalnymi znakami diakrytycznymi i laczy w jeden plik"
            >
              <Download className="h-4 w-4" />
              {isDownloadingAllPdfs ? 'Generowanie...' : 'Pobierz wszystkie PDFy'}
            </button>
            <button
              type="button"
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.zip';
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) handleBulkUpload(file);
                };
                input.click();
              }}
              disabled={isBulkUploading}
              className="btn-secondary text-sm flex items-center gap-1"
            >
              <Upload className="h-4 w-4" />
              {isBulkUploading ? 'Uploading...' : 'Upload ZIP'}
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowAddVariant(!showAddVariant)}
                className="btn-secondary text-sm flex items-center gap-1"
              >
                <Plus className="h-4 w-4" />
                Dodaj wariant
              </button>
              {showAddVariant && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto w-32">
                  {availableLangs.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-500">
                      Wszystkie jezyki dodane
                    </div>
                  ) : (
                    availableLangs.map((lang) => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => handleAddVariant(lang)}
                        className="block w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        {lang.toUpperCase()}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {(isBulkUploading || bulkUploadProgress) && (
          <div className={`text-sm px-3 py-2 rounded-lg ${
            bulkUploadHasErrors
              ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300'
              : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
          }`}>
            {isBulkUploading && (
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>{bulkUploadProgress || 'Processing...'}</span>
              </div>
            )}
            {!isBulkUploading && bulkUploadProgress && (
              <span>{bulkUploadProgress}</span>
            )}
          </div>
        )}

        {(isDownloadingAllPdfs || downloadAllProgress) && (
          <div className="text-sm px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
            {isDownloadingAllPdfs && (
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>{downloadAllProgress || 'Processing...'}</span>
              </div>
            )}
            {!isDownloadingAllPdfs && downloadAllProgress && (
              <span>{downloadAllProgress}</span>
            )}
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2">
                  Jezyk
                </th>
                <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2">
                  Status
                </th>
                <th className="text-right text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2">
                  Akcje
                </th>
              </tr>
            </thead>
            <tbody>
              {design.variants.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    Brak wariantow
                  </td>
                </tr>
              )}
              {design.variants.map((variant: DiplomaDesignVariantInfo) => (
                <tr
                  key={variant.id}
                  className="border-b border-gray-100 dark:border-gray-800 last:border-0"
                >
                  <td className="px-4 py-2.5 text-sm font-mono font-bold text-gray-900 dark:text-white">
                    {variant.language.toUpperCase()}
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusBadge
                      label={variant.pdfUrl ? 'Uploaded' : 'Brak'}
                      variant={variant.pdfUrl ? 'success' : 'neutral'}
                    />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {variant.pdfUrl && (
                        <button
                          type="button"
                          onClick={() => openPreviewModal(variant.id, variant.language)}
                          className="btn-secondary text-xs flex items-center gap-1"
                        >
                          <FileText className="h-3 w-3" />
                          Podglad PDF
                        </button>
                      )}
                      <input
                        ref={variantInputRef}
                        type="file"
                        accept=".pdf"
                        className="hidden"
                      />
                      {variant.pdfUrl && (
                        <a
                          href={variant.pdfUrl}
                          download={`${design.slug}_${variant.language}.pdf`}
                          className="btn-secondary text-xs flex items-center gap-1"
                        >
                          <Download className="h-3 w-3" />
                          Pobierz PDF
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = '.pdf';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) handleVariantUpload(variant.language, file);
                          };
                          input.click();
                        }}
                        disabled={isUploadingVariant === variant.language}
                        className="btn-secondary text-xs flex items-center gap-1"
                      >
                        <Upload className="h-3 w-3" />
                        {isUploadingVariant === variant.language
                          ? 'Uploading...'
                          : 'Upload PDF'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteVariant(variant.language)}
                        disabled={isDeletingVariant === variant.language}
                        className="btn-danger text-xs flex items-center gap-1"
                      >
                        <Trash2 className="h-3 w-3" />
                        {isDeletingVariant === variant.language ? '...' : 'Usun'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* PDF Preview */}
      {design.variants.length > 0 && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Podglad PDF
            </h3>
            <select
              value={previewVariant}
              onChange={(e) => setPreviewVariant(e.target.value)}
              className="input-field text-sm py-1.5 w-24"
            >
              {design.variants.map((v) => (
                <option key={v.id} value={v.language}>
                  {v.language.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          {isRenderingPdf && <LoadingSpinner size="sm" label="Renderowanie PDF..." />}

          <div ref={canvasContainerRef} className="relative inline-block" style={{ maxWidth: '100%' }}>
            <canvas
              ref={canvasRef}
              style={{ width: '100%', height: 'auto', display: 'block' }}
              className="rounded-lg border border-gray-200 dark:border-gray-700"
            />
            {/* Field overlays — use percentages so they scale with CSS-resized canvas */}
            {pdfAnnotations.map((ann, i) => {
              if (!pdfViewport) return null;
              const { width: cw, height: ch } = pdfViewport;
              if (!cw || !ch) return null;

              const [x1, y1, x2, y2] = ann.viewportRect;
              const leftPct = (x1 / cw) * 100;
              const topPct = (y1 / ch) * 100;
              const widthPct = ((x2 - x1) / cw) * 100;
              const heightPct = ((y2 - y1) / ch) * 100;

              const isTextField = ann.fieldType === 'Tx';
              const borderColor = isTextField
                ? 'border-blue-400 bg-blue-400/10'
                : 'border-amber-400 bg-amber-400/10';

              return (
                <div
                  key={i}
                  className={`absolute border ${borderColor} pointer-events-none`}
                  style={{
                    left: `${leftPct}%`,
                    top: `${topPct}%`,
                    width: `${widthPct}%`,
                    height: `${heightPct}%`,
                  }}
                >
                  <span className="absolute inset-0 flex items-center justify-center text-[7px] font-mono text-gray-700/70 dark:text-gray-300/70 whitespace-nowrap overflow-hidden px-0.5 leading-tight">
                    <span className="truncate">
                      {ann.fieldName || 'field'}
                      {ann.defaultAppearanceData?.fontName && (
                        <span className="ml-0.5 opacity-60">
                          ({ann.defaultAppearanceData.fontName}
                          {ann.defaultAppearanceData.fontSize
                            ? ` ${ann.defaultAppearanceData.fontSize}pt`
                            : ''}
                          )
                        </span>
                      )}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fonts */}
      <div className="card space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Czcionki designu
          </h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleValidateFonts}
              disabled={isValidatingFonts || !previewVariant}
              className="btn-secondary text-sm flex items-center gap-1"
              title={!previewVariant ? 'Wybierz wariant PDF do sprawdzenia' : ''}
            >
              {isValidatingFonts ? 'Sprawdzam...' : 'Sprawdz czcionki'}
            </button>
            <input
              ref={fontInputRef}
              type="file"
              accept=".ttf"
              onChange={handleDesignFontUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fontInputRef.current?.click()}
              disabled={isUploadingFont}
              className="btn-secondary text-sm flex items-center gap-1"
            >
              <Upload className="h-4 w-4" />
              {isUploadingFont ? 'Uploading...' : 'Dodaj czcionke TTF'}
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2">
                  Nazwa (DB)
                </th>
                <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2">
                  Plik w blob storage
                </th>
                <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2 w-16">
                  Primary
                </th>
                <th className="text-right text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2">
                  Akcje
                </th>
              </tr>
            </thead>
            <tbody>
              {design.fonts.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    Brak czcionek
                  </td>
                </tr>
              )}
              {design.fonts.map((font) => (
                <tr
                  key={font.id}
                  className={`border-b border-gray-100 dark:border-gray-800 last:border-0 ${
                    font.isPrimary
                      ? 'bg-amber-50/50 dark:bg-amber-900/10'
                      : ''
                  }`}
                >
                  <td className="px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-white">
                    {font.fontName}
                  </td>
                  <td className="px-4 py-2.5">
                    <button
                      type="button"
                      onClick={() => handleRenameFont(font.id, font.fontName)}
                      className="text-xs font-mono text-brand-600 dark:text-brand-400 hover:underline cursor-pointer"
                      title="Kliknij aby zmienic nazwe"
                    >
                      {font.blobPath.split('/').pop() || font.blobPath}
                    </button>
                  </td>
                  <td className="px-4 py-2.5">
                    {font.isPrimary && (
                      <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => handleDeleteDesignFont(font.id)}
                      disabled={isDeletingDesignFont === font.id}
                      className="btn-danger text-xs"
                    >
                      {isDeletingDesignFont === font.id ? '...' : 'Usun'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Font Validation Results */}
      {fontValidation && (
        <div className="card space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Raport pokrycia czcionek ({previewVariant?.toUpperCase()})
          </h3>

          <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2 w-8"></th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2">Pole</th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2">BaseFont w PDF</th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2">Resolved to</th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2">Size</th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2">Color</th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {fontValidation.map((r, i) => {
                  const cfg = STATUS_CONFIG[r.status];
                  return (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                      <td className="px-4 py-2 text-center">{cfg.icon}</td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white">{r.fieldName}</td>
                      <td className="px-4 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">{r.baseFont || '-'}</td>
                      <td className="px-4 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">{r.resolvedFont}</td>
                      <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">{r.fontSize ? `${r.fontSize}pt` : '-'}</td>
                      <td className="px-4 py-2">
                        {r.fontColor && (
                          <span className="inline-flex items-center gap-1">
                            <span className="w-3 h-3 rounded-full border border-gray-300 dark:border-gray-600" style={{ backgroundColor: r.fontColor }} />
                            <span className="text-[10px] font-mono text-gray-500">{r.fontColor}</span>
                          </span>
                        )}
                      </td>
                      <td className={`px-4 py-2 text-xs font-medium ${cfg.color}`}>{cfg.label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          {(() => {
            const matched = fontValidation.filter(r => r.status === 'EXACT_MATCH' || r.status === 'FAMILY_MATCH').length;
            const fallback = fontValidation.filter(r => r.status === 'DESIGN_FALLBACK' || r.status === 'DEFAULT_FALLBACK').length;
            const noInfo = fontValidation.filter(r => r.status === 'NO_FONT_INFO').length;
            const missing = fontValidation.filter(r => r.status === 'DESIGN_FALLBACK' || r.status === 'DEFAULT_FALLBACK');

            return (
              <div className="space-y-2 text-sm">
                <div className="flex gap-4">
                  <span className="text-emerald-600 dark:text-emerald-400">✅ Matched: {matched}/{fontValidation.length}</span>
                  {fallback > 0 && <span className="text-amber-600 dark:text-amber-400">⚠️ Fallback: {fallback}/{fontValidation.length}</span>}
                  {noInfo > 0 && <span className="text-gray-500">❓ No info: {noInfo}/{fontValidation.length}</span>}
                </div>
                {missing.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">
                      ⚠️ Wgraj te pliki TTF do <span className="font-mono">resources/fonts/</span>:
                    </p>
                    <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-0.5">
                      {[...new Set(missing.map(r => r.baseFont))].filter(Boolean).map(name => (
                        <li key={name!} className="font-mono">
                          → {name}.ttf {missing.find(r => r.baseFont === name)?.family !== name && (
                            <span className="text-amber-500">(lub {missing.find(r => r.baseFont === name)?.family}.ttf)</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Danger Zone */}
      <div className="card border-red-300 dark:border-red-800 space-y-3">
        <h3 className="text-sm font-semibold text-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          Strefa niebezpieczna
        </h3>
        {design.usageCount > 0 && (
          <p className="text-xs text-red-600 dark:text-red-400">
            Ten design jest uzywany w {design.usageCount} szablonach.
            Nie mozna go usunac.
          </p>
        )}
        {showDeleteConfirm ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-red-600 dark:text-red-400">
              Na pewno usunac ten design?
            </span>
            <button
              type="button"
              onClick={handleDeleteDesign}
              disabled={isDeleting}
              className="btn-danger text-xs"
            >
              {isDeleting ? 'Usuwanie...' : 'Tak, usun'}
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(false)}
              className="btn-secondary text-xs"
            >
              Anuluj
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={design.usageCount > 0}
            className="btn-danger text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 className="h-4 w-4" />
            Usun design
          </button>
        )}
      </div>

      {/* Font Name Modal */}
      <InputModal
        isOpen={fontNameModal !== null}
        title={fontNameModal?.mode === 'upload' ? 'Nazwa czcionki' : 'Zmien nazwe czcionki'}
        label={fontNameModal?.mode === 'upload'
          ? 'Nazwa czcionki (musi pasowac do definicji w PDF):'
          : 'Nowa nazwa pliku czcionki (bez .ttf):'}
        defaultValue={fontNameModal?.defaultValue || ''}
        onConfirm={(value) => {
          if (fontNameModal?.mode === 'upload') {
            handleFontUploadConfirm(value);
          } else {
            handleRenameConfirm(value);
          }
        }}
        onCancel={() => setFontNameModal(null)}
      />

      <PreviewPdfModal
        isOpen={!!previewModal}
        language={previewModal?.language || ''}
        isGenerating={isGeneratingPreview}
        previewUrl={previewUrl}
        settings={previewSettings}
        error={previewError}
        onGenerate={handlePreviewPdf}
        onUploadImage={handleUploadPreviewImage}
        onClose={() => setPreviewModal(null)}
      />
    </div>
  );
}

// ---- Main Page ----

export function CertificateTemplatesPage() {
  const [selectedDesign, setSelectedDesign] =
    useState<DiplomaDesignOverview | null>(null);

  return (
    <div className="space-y-6">
      {!selectedDesign && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <FileText className="h-6 w-6 text-brand-600 dark:text-brand-400" />
            Szablony certyfikatow
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Zarzadzanie designami certyfikatow i czcionkami
          </p>
        </div>
      )}

      {selectedDesign ? (
        <DesignDetailView
          initialDesign={selectedDesign}
          onBack={() => setSelectedDesign(null)}
        />
      ) : (
        <GalleryView onSelect={setSelectedDesign} />
      )}
    </div>
  );
}
